import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { verifyAccount, resendVerificationOtp } from "../../services/auth";

export default function VerifyAccount() {
  const { userId: paramUserId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const initialUserId = location?.state?.userId || paramUserId || "";
  const initialEmail = location?.state?.email || "";
  const initialOtp = location?.state?.otp || "";

  const [userId, setUserId] = useState(initialUserId);
  const [email, setEmail] = useState(initialEmail);
  const [otp, setOtp] = useState(initialOtp);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    if (!timerRef.current) {
      timerRef.current = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            return 0;
          }
          return s - 1;
        });
      }, 1000);
    }
  }, [secondsLeft]);

  const startCountdown = (sec = 60) => {
    setSecondsLeft(sec);
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!userId || !otp) {
      setError("يرجى إدخال رمز التحقق.");
      return;
    }
    setLoading(true);
    try {
      await verifyAccount({ user_id: userId, otp });
      setSuccess("تم تفعيل الحساب. سيتم التوجيه إلى صفحة تسجيل الدخول.");
      setTimeout(() => navigate("/login"), 1200);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "فشل التحقق.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError("");
    setSuccess("");
    if (!email) {
      setError("يرجى إدخال البريد الإلكتروني لإعادة إرسال الكود.");
      return;
    }
    setResendLoading(true);
    try {
      await resendVerificationOtp({ email });
      setSuccess("تم إرسال رمز التحقق. تحقق من بريدك.");
      startCountdown(60);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || "فشل إعادة الإرسال.";
      setError(msg);
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: "-50%",
          right: "-20%",
          width: "80%",
          height: "80%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(213, 170, 47, 0.08) 0%, transparent 70%)",
          animation: "float 20s ease-in-out infinite",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: "-50%",
          left: "-20%",
          width: "80%",
          height: "80%",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(213, 170, 47, 0.08) 0%, transparent 70%)",
          animation: "float 15s ease-in-out infinite reverse",
        },
        "@keyframes float": {
          "0%, 100%": { transform: "translate(0, 0)" },
          "50%": { transform: "translate(50px, 50px)" },
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 4,
            border: "1px solid",
            borderColor: "grey.100",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
            backdropFilter: "blur(10px)",
            animation: "fadeInUp 0.6s ease-out",
            "@keyframes fadeInUp": {
              from: { opacity: 0, transform: "translateY(30px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                mx: "auto",
                mb: 2,
                borderRadius: 3,
                background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 24px rgba(213, 170, 47, 0.3)",
              }}
            >
              <VerifiedUserIcon sx={{ fontSize: 40, color: "white" }} />
            </Box>
            <Typography
              variant="h4"
              fontWeight={800}
              sx={{
                mb: 1,
                background: "linear-gradient(135deg, #d5aa2f 0%, #b08c26 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              تفعيل الحساب
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              أدخل رمز التحقق المرسل إلى بريدك الإلكتروني
            </Typography>
            {email && (
              <Typography
                variant="caption"
                color="primary.main"
                fontWeight={700}
                sx={{ mt: 1, display: "block" }}
              >
                {email}
              </Typography>
            )}
          </Box>

          {/* Alerts */}
          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 3,
                animation: "shake 0.5s ease-in-out",
                "@keyframes shake": {
                  "0%, 100%": { transform: "translateX(0)" },
                  "25%": { transform: "translateX(-10px)" },
                  "75%": { transform: "translateX(10px)" },
                },
              }}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 3 }}>
              {success}
            </Alert>
          )}

          {/* Hidden User ID Field */}
          <input type="hidden" value={userId} />

          {/* Verification Form */}
          <form onSubmit={handleVerify}>
            <Stack spacing={3}>
              <TextField
                label="رمز التحقق (OTP)"
                name="otp"
                placeholder="أدخل الرمز المكون من 6 أرقام"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                fullWidth
                inputProps={{
                  maxLength: 6,
                  pattern: "[0-9]*",
                }}
                sx={{
                  "& input": {
                    textAlign: "center",
                    fontSize: "1.5rem",
                    letterSpacing: "0.5em",
                    fontWeight: 700,
                  },
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{
                  py: 1.5,
                  fontSize: "1rem",
                  fontWeight: 800,
                  borderRadius: 3,
                  textTransform: "none",
                  background: "linear-gradient(135deg, #d5aa2f 0%, #e6c76a 100%)",
                  boxShadow: "0 4px 16px rgba(213, 170, 47, 0.35)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #b08c26 0%, #d5aa2f 100%)",
                    boxShadow: "0 6px 24px rgba(213, 170, 47, 0.45)",
                  },
                  "&:disabled": {
                    background: "linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)",
                  },
                }}
              >
                {loading ? "جاري التحقق..." : "تحقق من الحساب"}
              </Button>
            </Stack>
          </form>

          {/* Resend Section */}
          <Box
            sx={{
              mt: 3,
              pt: 3,
              borderTop: "1px solid",
              borderColor: "grey.100",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              لم تستلم الرمز؟
            </Typography>
            <Button
              variant="outlined"
              onClick={handleResend}
              disabled={resendLoading || secondsLeft > 0}
              sx={{
                borderRadius: 3,
                px: 3,
                fontWeight: 700,
                textTransform: "none",
                borderWidth: 2,
                "&:hover": {
                  borderWidth: 2,
                },
              }}
            >
              {resendLoading ? (
                <CircularProgress size={20} />
              ) : secondsLeft > 0 ? (
                `إعادة الإرسال بعد ${secondsLeft}s`
              ) : (
                "إعادة إرسال الرمز"
              )}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
